import React, { useEffect, useState, useCallback } from 'react';
import { pursuitApi } from '../../api/client';

// Seller pursuit: Compliance Matrix. AI shreds uploaded solicitation docs
// (Sections L/M, SOW/PWS) into requirement rows; the user tracks coverage.

interface Props { oppId: string; }

type Row = {
  id: string; source: string; requirement_text: string;
  response_owner: string; status: string; evidence_ref: string;
};

const STATUSES = ['not_started', 'in_progress', 'covered'];
const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  not_started: { bg: '#f1f5f9', fg: '#475569' },
  in_progress: { bg: '#fef9c3', fg: '#854d0e' },
  covered: { bg: '#dcfce7', fg: '#166534' },
};

export default function ComplianceMatrixTab({ oppId }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [shredding, setShredding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pursuitApi.getComplianceMatrix(oppId);
      setRows(Array.isArray(res.compliance_matrix) ? res.compliance_matrix : []);
      setDirty(false);
    } catch (e: any) { setError(e?.message || 'Could not load matrix.'); }
    setLoading(false);
  }, [oppId]);

  useEffect(() => { load(); }, [load]);

  const shred = async () => {
    setShredding(true); setError(''); setMsg('');
    try {
      const res = await pursuitApi.shredComplianceMatrix(oppId);
      const shredded: Row[] = res.compliance_matrix || [];
      // Merge: keep existing rows, append newly shredded (avoid wiping user edits).
      setRows(prev => {
        const existingText = new Set(prev.map(r => r.requirement_text.trim().toLowerCase()));
        const additions = shredded.filter(r => !existingText.has((r.requirement_text || '').trim().toLowerCase()));
        return [...prev, ...additions];
      });
      setDirty(true);
      setMsg(`Extracted ${res.shredded ?? shredded.length} requirement(s). Review, assign owners, then Save.`);
    } catch (e: any) { setError(e?.message || 'Shred failed. Ensure the solicitation is uploaded to Documents.'); }
    setShredding(false);
  };

  const save = async () => {
    setSaving(true); setError(''); setMsg('');
    try {
      await pursuitApi.saveComplianceMatrix(oppId, rows);
      setDirty(false); setMsg('Matrix saved.');
    } catch (e: any) { setError(e?.message || 'Save failed.'); }
    setSaving(false);
  };

  const update = (id: string, field: keyof Row, value: string) => {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, [field]: value } : r))); setDirty(true);
  };
  const remove = (id: string) => { setRows(rs => rs.filter(r => r.id !== id)); setDirty(true); };
  const addRow = () => {
    setRows(rs => [...rs, { id: `cm-manual-${Date.now()}`, source: 'Other', requirement_text: '', response_owner: '', status: 'not_started', evidence_ref: '' }]);
    setDirty(true);
  };

  const covered = rows.filter(r => r.status === 'covered').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Compliance Matrix</h2>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0', maxWidth: 640 }}>
            Every requirement the proposal must satisfy, extracted from the solicitation (Sections L/M,
            SOW/PWS). Track coverage so nothing is missed at submission.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={shred} disabled={shredding}
            style={{ padding: '9px 14px', background: shredding ? '#93c5fd' : '#4338ca', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: shredding ? 'default' : 'pointer' }}>
            {shredding ? 'Shredding…' : 'Shred solicitation (AI)'}
          </button>
          <button onClick={save} disabled={saving || !dirty}
            style={{ padding: '9px 14px', background: dirty ? '#10b981' : '#e2e8f0', color: dirty ? 'white' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: dirty && !saving ? 'pointer' : 'default' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {msg && <div style={{ color: '#166534', background: '#dcfce7', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      {rows.length > 0 && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Coverage: <strong>{covered}/{rows.length}</strong> requirements marked covered.
        </div>
      )}

      {loading ? (
        <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, border: '1px dashed #cbd5e1', borderRadius: 12, textAlign: 'center', color: '#64748b' }}>
          No requirements yet. Upload the solicitation to the Documents panel, then click
          <strong> Shred solicitation (AI)</strong> — or add rows manually.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={th}>Src</th><th style={th}>Requirement</th><th style={th}>Owner</th><th style={th}>Status</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td style={td}>
                    <select value={r.source} onChange={e => update(r.id, 'source', e.target.value)} style={sel}>
                      {['L', 'M', 'SOW', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <textarea value={r.requirement_text} onChange={e => update(r.id, 'requirement_text', e.target.value)}
                      rows={2} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: 6, fontSize: 13, resize: 'vertical' }} />
                  </td>
                  <td style={td}>
                    <input value={r.response_owner} onChange={e => update(r.id, 'response_owner', e.target.value)}
                      placeholder="owner" style={{ width: 90, border: '1px solid #e2e8f0', borderRadius: 6, padding: 6, fontSize: 12 }} />
                  </td>
                  <td style={td}>
                    <select value={r.status} onChange={e => update(r.id, 'status', e.target.value)}
                      style={{ ...sel, background: (STATUS_STYLE[r.status] || STATUS_STYLE.not_started).bg, color: (STATUS_STYLE[r.status] || STATUS_STYLE.not_started).fg, fontWeight: 600 }}>
                      {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <button onClick={() => remove(r.id)} aria-label="Remove row"
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={addRow} style={{ marginTop: 12, padding: '7px 14px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        + Add requirement
      </button>
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 };
const td: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
const sel: React.CSSProperties = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 12 };
